import { useState, useEffect, useRef } from 'react';

interface PeriodFilterProps {
  onPeriodChange?: (period: string) => void;
}

const periods = [
  { value: '24h', label: 'Dernières 24h' },
  { value: '7d', label: '7 derniers jours' },
  { value: '14d', label: '14 derniers jours' },
  { value: '30d', label: '30 derniers jours' },
  { value: '90d', label: '90 derniers jours' }
];

export const PeriodFilter = ({ onPeriodChange }: PeriodFilterProps) => {
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePeriodChange = (value: string) => {
    setSelectedPeriod(value);
    setDropdownOpen(false);
    onPeriodChange?.(value);
  };

  const getDisplayLabel = () => {
    const period = periods.find(p => p.value === selectedPeriod);
    return period?.label || '7 derniers jours';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="p-3 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-center items-center gap-1 whitespace-nowrap"
      >
        <div data-svg-wrapper className="relative">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.3335 1.33334V3.33334" stroke="#40E0D0" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10.6665 1.33334V3.33334" stroke="#40E0D0" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2.3335 6.06H13.6668" stroke="#40E0D0" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 5.66668V11.3333C14 13.3333 13 14.6667 10.6667 14.6667H5.33333C3 14.6667 2 13.3333 2 11.3333V5.66668C2 3.66668 3 2.33334 5.33333 2.33334H10.6667C13 2.33334 14 3.66668 14 5.66668Z" stroke="#40E0D0" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7.99715 9.13335H8.00314" stroke="#40E0D0" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M5.52938 9.13335H5.53537" stroke="#40E0D0" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M5.52938 11.1334H5.53537" stroke="#40E0D0" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div className="justify-center text-gray-800 text-xs font-medium font-['Bricolage_Grotesque'] whitespace-nowrap">
          {getDisplayLabel()}
        </div>
        <div data-svg-wrapper className="relative">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.3332 6.66667L7.99984 10L4.6665 6.66667" stroke="#8F949D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </button>
      
      {dropdownOpen && (
        <div className="absolute top-full left-0 mt-1 p-1 bg-white rounded-[10px] shadow-[0px_0px_15px_0px_rgba(0,0,0,0.10)] outline outline-[0.50px] outline-offset-[-0.50px] outline-neutral-400/20 z-50 min-w-max">
          {periods.map((period) => (
            <div 
              key={period.value}
              onClick={() => handlePeriodChange(period.value)}
              className={`px-2 py-1.5 rounded-md flex justify-between items-center cursor-pointer hover:bg-teal-400/10 whitespace-nowrap ${selectedPeriod === period.value ? 'bg-teal-400/10' : ''}`}
            >
              <div className={`text-xs font-normal font-['Bricolage_Grotesque'] ${selectedPeriod === period.value ? 'text-teal-400' : 'text-gray-800'}`}>
                {period.label}
              </div>
              {selectedPeriod === period.value && (
                <div data-svg-wrapper className="relative">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.0625 6.5625L4.6875 9.1875L9.9375 3.5625" stroke="#40E0D0" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};